import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert,
  Snackbar, Stack, CircularProgress, Select, MenuItem,
  FormControl, InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { tenantApi } from '../../api/tenantApi';
import { useReadOnly } from '../../context/ReadOnlyContext';

const ROLES = ['admin', 'manager', 'staff', 'viewer'];
const ROLE_COLOR = { admin: 'error', manager: 'warning', staff: 'primary', viewer: 'default' };

export default function UsersPage() {
  const { isReadOnly } = useReadOnly();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'staff' });
  const [saving, setSaving] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editRole, setEditRole] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tenantApi.listUsers();
      setUsers(data.users || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async () => {
    setSaving(true);
    try {
      await tenantApi.inviteUser(inviteForm);
      setToast({ open: true, message: `Invitation sent to ${inviteForm.email}.`, severity: 'success' });
      setInviteOpen(false);
      setInviteForm({ email: '', role: 'staff' });
      load();
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
    finally { setSaving(false); }
  };

  const handleUpdateRole = async () => {
    try {
      await tenantApi.updateUser(editTarget.userId, { role: editRole });
      setToast({ open: true, message: 'Role updated.', severity: 'success' });
      setEditTarget(null);
      load();
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
  };

  const handleDisable = async (user) => {
    try {
      await tenantApi.disableUser(user.userId);
      setToast({ open: true, message: `${user.email} disabled.`, severity: 'info' });
      load();
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
  };

  const handleRemove = async (user) => {
    if (!window.confirm(`Remove ${user.email} from this organization?`)) { return; }
    try {
      await tenantApi.removeUser(user.userId);
      setToast({ open: true, message: `${user.email} removed.`, severity: 'info' });
      load();
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Users</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setInviteOpen(true)} disabled={isReadOnly}>
          Invite User
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Member Since</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No users.</TableCell></TableRow>
            ) : users.map((u) => (
              <TableRow key={u.userId} hover>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Chip label={u.role} size="small" color={ROLE_COLOR[u.role] || 'default'} variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip label={u.enabled ? 'Active' : 'Disabled'} size="small"
                    color={u.enabled ? 'success' : 'default'} />
                </TableCell>
                <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
                  {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit role">
                    <IconButton size="small" onClick={() => { setEditTarget(u); setEditRole(u.role); }} disabled={isReadOnly}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Disable user">
                    <IconButton size="small" onClick={() => handleDisable(u)} disabled={isReadOnly || !u.enabled}>
                      <BlockIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove user">
                    <IconButton size="small" color="error" onClick={() => handleRemove(u)} disabled={isReadOnly}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Email Address *" type="email" size="small" fullWidth
              value={inviteForm.email}
              onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} />
            <FormControl size="small" fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={inviteForm.role} label="Role"
                onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}>
                {ROLES.map((r) => <MenuItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleInvite} disabled={saving || !inviteForm.email}>
            {saving ? 'Sending…' : 'Send Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit role dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Role — {editTarget?.email}</DialogTitle>
        <DialogContent>
          <FormControl size="small" fullWidth sx={{ mt: 1 }}>
            <InputLabel>Role</InputLabel>
            <Select value={editRole} label="Role" onChange={(e) => setEditRole(e.target.value)}>
              {ROLES.map((r) => <MenuItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateRole} disabled={editRole === editTarget?.role}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
